import React, { useMemo, useState } from 'react';
import { MenuCategory, MenuItem } from '../types/api';
import { useLanguage } from '../contexts/LanguageContext';
import { isLeafCategory } from '../utils/categoryTree';
import { formatTenge } from '../utils/money';
import { fill } from '../utils/template';

/** `category` — один раздел целиком, `items` — галочки у позиций. */
export type TreeSelectMode = 'category' | 'items';

interface TreeSelectProps {
  categories: MenuCategory[];
  items: MenuItem[];
  mode: TreeSelectMode;
  selectedCategoryId?: number | null;
  selectedItemIds?: number[];
  onCategoryChange?: (categoryId: number | null) => void;
  onItemsChange?: (itemIds: number[]) => void;
  disabled?: boolean;
}

/** Предохранитель обхода предков: глубина каталога — 4 уровня. */
const MAX_DEPTH_GUARD = 16;

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
      <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Сколько позиций в разделе вместе с подразделами: счёт идёт вверх по предкам. */
function countBySubtree(categories: MenuCategory[], items: MenuItem[]): Map<number, number> {
  const parentOf = new Map(categories.map((category) => [category.id, category.parent_id]));
  const counts = new Map<number, number>();

  items.forEach((item) => {
    let id: number | null | undefined = item.menu_category_id;
    let guard = 0;
    while (id != null && guard++ < MAX_DEPTH_GUARD) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
      id = parentOf.get(id);
    }
  });

  return counts;
}

/**
 * Дерево меню в форме скидки.
 *
 * Два режима — два разных охвата, и в одном дереве они больше не смешиваются.
 * Раньше клик по разделу выбирал его целиком и ставил галочки всем позициям;
 * снятые потом галочки ничего не меняли — уходил раздел, и скидка молча
 * ложилась на весь раздел. Теперь в режиме `category` выбирается только
 * раздел, в режиме `items` — только позиции.
 *
 * Данные приходят из формы готовыми: дерево ничего не грузит само. Прежняя
 * подгрузка по клику читала позиции из устаревшего замыкания, и после
 * загрузки галочки не ставились.
 */
export function TreeSelect({
  categories,
  items,
  mode,
  selectedCategoryId = null,
  selectedItemIds = [],
  onCategoryChange,
  onItemsChange,
  disabled = false,
}: TreeSelectProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  const leafIds = useMemo(
    () => new Set(categories.filter((category) => isLeafCategory(category, categories)).map((c) => c.id)),
    [categories],
  );
  const byId = useMemo(() => new Map(categories.map((category) => [category.id, category])), [categories]);

  const itemsByCategory = useMemo(() => {
    const map = new Map<number, MenuItem[]>();
    items.forEach((item) => {
      const bucket = map.get(item.menu_category_id);
      if (bucket) bucket.push(item);
      else map.set(item.menu_category_id, [item]);
    });
    return map;
  }, [items]);

  const selected = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);
  const totals = useMemo(() => countBySubtree(categories, items), [categories, items]);
  const checkedTotals = useMemo(
    () => countBySubtree(categories, items.filter((item) => selected.has(item.id))),
    [categories, items, selected],
  );

  // Ветки раскрыты сразу — иначе подразделы не видно и кажется, что меню
  // пусто. Позиции раскрыты там, где уже стоят галочки, и в единственном
  // разделе: прятать за кликом весь список незачем.
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    const open = new Set(categories.filter((category) => !leafIds.has(category.id)).map((c) => c.id));
    if (mode === 'items') {
      items.forEach((item) => {
        if (selectedItemIds.includes(item.id)) open.add(item.menu_category_id);
      });
      if (leafIds.size === 1) leafIds.forEach((id) => open.add(id));
    }
    return open;
  });

  const toggleExpanded = (categoryId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  /** Строка видна, пока раскрыты все её предки. */
  const isVisible = (category: MenuCategory): boolean => {
    let parentId = category.parent_id;
    let guard = 0;
    while (parentId != null && guard++ < MAX_DEPTH_GUARD) {
      if (!expanded.has(parentId)) return false;
      parentId = byId.get(parentId)?.parent_id ?? null;
    }
    return true;
  };

  const toggleItem = (itemId: number) => {
    onItemsChange?.(
      selected.has(itemId) ? selectedItemIds.filter((id) => id !== itemId) : [...selectedItemIds, itemId],
    );
  };

  const toggleAll = (categoryId: number) => {
    const ids = (itemsByCategory.get(categoryId) ?? []).map((item) => item.id);
    const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));

    onItemsChange?.(
      allChecked
        ? selectedItemIds.filter((id) => !ids.includes(id))
        : [...selectedItemIds, ...ids.filter((id) => !selected.has(id))],
    );
  };

  if (categories.length === 0) {
    return (
      <p className="qp-field-hint" style={{ margin: 0 }}>
        {strings.menuEmpty}
      </p>
    );
  }

  const renderCategoryRow = (category: MenuCategory, leaf: boolean, open: boolean) => {
    const picked = selectedCategoryId === category.id;
    const count = fill(strings.treeCount, { count: totals.get(category.id) ?? 0 });

    return (
      <div className="qp-row">
        <button
          type="button"
          className="qp-tree-pick"
          onClick={() => onCategoryChange?.(picked ? null : category.id)}
          disabled={disabled}
          aria-pressed={picked}
        >
          <span className={`qp-pick qp-pick--radio${picked ? ' qp-pick--on' : ''}`} aria-hidden>
            {picked && <CheckIcon />}
          </span>
          <span style={{ minWidth: 0 }}>
            <span className="qp-cell-title">{category.name}</span>
            {/* Скидка на ветку действует и на её подразделы — это считает бэк,
                поэтому выбор ветки не бессмыслен. */}
            <span className="qp-cell-sub">{leaf ? count : `${strings.treeBranch} · ${count}`}</span>
          </span>
        </button>

        {!leaf && (
          <div className="qp-row-tail">
            <button
              type="button"
              className={`qp-tree-chev${open ? ' qp-tree-chev--open' : ''}`}
              onClick={() => toggleExpanded(category.id)}
              disabled={disabled}
              aria-expanded={open}
              aria-label={open ? strings.treeCollapse : strings.treeExpand}
            >
              <ChevronIcon />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderItemsRow = (category: MenuCategory, leaf: boolean, open: boolean) => {
    const leafItems = itemsByCategory.get(category.id) ?? [];
    const checked = checkedTotals.get(category.id) ?? 0;
    const allChecked = leafItems.length > 0 && leafItems.every((item) => selected.has(item.id));
    const base = leaf ? fill(strings.treeCount, { count: leafItems.length }) : strings.treeBranch;
    const sub = checked > 0 ? `${base} · ${fill(strings.treeChecked, { count: checked })}` : base;

    return (
      <>
        <div className="qp-row">
          {/* Имя раздела раскрывает его: выбирать здесь можно только позиции. */}
          <button
            type="button"
            className="qp-tree-pick"
            onClick={() => toggleExpanded(category.id)}
            disabled={disabled}
            aria-expanded={open}
          >
            <span className={`qp-tree-caret${open ? ' qp-tree-caret--open' : ''}`} aria-hidden>
              <ChevronIcon />
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="qp-cell-title">{category.name}</span>
              <span className="qp-cell-sub">{sub}</span>
            </span>
          </button>

          {leaf && leafItems.length > 0 && (
            <div className="qp-row-tail">
              <button
                type="button"
                className="qp-btn qp-btn--sm qp-btn--outline"
                onClick={() => toggleAll(category.id)}
                disabled={disabled}
                aria-label={`${allChecked ? strings.treeUnselectAll : strings.treeSelectAll}: ${category.name}`}
              >
                {allChecked ? strings.treeUnselectAll : strings.treeSelectAll}
              </button>
            </div>
          )}
        </div>

        {leaf && open && (
          <div className="qp-tree-items">
            {leafItems.length > 0 ? (
              leafItems.map((item) => (
                <label key={item.id} className="qp-tree-item">
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                    disabled={disabled}
                  />
                  <span className="qp-cell-title" style={{ flex: 1, minWidth: 0 }}>
                    {item.name}
                  </span>
                  <span className="qp-mono qp-faint" style={{ fontSize: 12 }}>
                    {formatTenge(item.price)}
                  </span>
                </label>
              ))
            ) : (
              <p className="qp-tree-empty">{strings.treeEmpty}</p>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="qp-tree">
      <div className="qp-rows">
        {categories.filter(isVisible).map((category) => {
          const leaf = leafIds.has(category.id);
          const open = expanded.has(category.id);

          return (
            <div key={category.id} className="qp-tree-group" style={{ marginLeft: category.depth * 16 }}>
              {mode === 'category'
                ? renderCategoryRow(category, leaf, open)
                : renderItemsRow(category, leaf, open)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
